import { BBModel } from '../bb_model';
import { convertTimeToMoment } from '../utils/convert_time';
import { personSelectionService } from './person_selection';
import { bbTimeZone } from '../utils/time_zone';

/**
 * @description Entry point function to query time data api
 *
 * @param {Object} params - The payload to be sent to the API
 * to make a valid query
 * Values can consist of the following:
 * date, start_date, cItem, end_date, time_zone, duration, location,
 * num_resources, people_ids, resource_ids, company, available, item_link
 *
 * @return {Promise} deferred.promise - Used to chain call to query function
 */
function query(params) {
    return new Promise((resolve, reject) => {
        const queryDefaults = getQueryParameterDefaults(params);

        if (!queryDefaults.start_date) {
            reject('No date set');

            return;
        }

        const extra = getQueryParameters(queryDefaults);

        if (params.cancelToken) {
          extra.cancelToken = params.cancelToken;
        }

        if (params.datesOnly) {
            return queryDates(queryDefaults, extra, resolve, reject);
        }

        if (params.singleDay) {
            return queryDayTimes(queryDefaults, extra, resolve, reject);
        }

        if (queryDefaults.item_link && (queryDefaults.item_link.$has('service_items_times') || queryDefaults.item_link.$has('times'))) {
            if (extra.event_id) {
                queryDefaults.item_link = params.company;
            }
            if (extra.date) extra.start_date = extra.date;
            queryTime(queryDefaults, extra, resolve, reject);
        }  else {
            console.warn('Unable to retrieve times: service does not have a \'times\' link. This could be because the service is day/week based.');
            reject('No day data');
        }

    });
}

/**
 * @description Query a set of basket items for the same time data
 *
 * @param {Object} prms - An object containing an array of BasketItems
 *
 * @return {Promise} defer.promise - Used to chain call to query function
 */
function queryItems(prms) {
    return new Promise((resolve, reject) => {
        const pslots = [];

        for (const item of prms.items) {
            pslots.push(query({
                company: prms.company,
                cItem: item,
                date: prms.start_date,
                end_date: prms.end_date,
                client: prms.client,
                available: 1
            }));
        }

        Promise.all(pslots)
            .then((res) => {
                resolve(res);
            })
            .catch((err) => {
                reject();
            });

    });
}

// This variable is used to hold a copy of the original start date that was requested because the getQueryParameterDefaults
// function mutates the start date to have a different start time if you are using different time zones.
// This is apparently because of a bug in the API that would not send the full range of times to the front end without
// us requesting an entire day extra. Later on in the resolve of queryTime the original start date is restored.
let originalStartDate;

/**
 * @description Sets default values and returns
 * basic object used to build the actual query parameter object
 *
 * @param {Object} params - Default parameters to be set,
 * can include any of the following:
 * date, start_date, cItem, end_date, time_zone, duration, location,
 * num_resources, people_ids, resource_ids, company, available, item_link
 *
 * @return {Object} defaults - Used to build query parameter object
 */
function getQueryParameterDefaults(params) {
    const defaults = Object.assign({}, params);

    if (!defaults.start_date) {
        if (defaults.date) {
            defaults.start_date = defaults.date;
        } else if (defaults.cItem.date) {
            defaults.start_date = defaults.cItem.date.date;
        }
    }

    if (defaults.start_date) {
        originalStartDate = defaults.start_date.clone();

        if (!defaults.end_date) {
            defaults.end_date = defaults.start_date.clone();
        }

        if (bbTimeZone.getDisplay() && bbTimeZone.getDisplay() !== bbTimeZone.getCompany()) {
            if (bbTimeZone.getCompanyUTCOffset() < bbTimeZone.getDisplayUTCOffset()) {
                defaults.start_date = defaults.start_date.clone().subtract(1, 'day');
            } else if (bbTimeZone.getCompanyUTCOffset() > bbTimeZone.getDisplayUTCOffset() && defaults.end_date) {
                defaults.end_date = defaults.end_date.clone().add(1, 'day');
            }

            defaults.time_zone = bbTimeZone.getDisplay();
        }
    }

    if (defaults.duration == null) {
        if (defaults.cItem && defaults.cItem.duration) {
            defaults.duration = defaults.cItem.duration;
        }
    }

    if (!defaults.item_link) {
        if (defaults.cItem && defaults.cItem.days_link) {
            defaults.item_link = defaults.cItem.days_link;
        }
    }

    return defaults;
}

/**
 * @description Returns object suitable for sending to the API
 * to query for time data
 *
 * @param {Object} params - Default parameters to be sent.
 * This object should be built form getQueryParameterDefaults
 * but not restricted to it.
 * The parameter can include any of the following:
 * date, start_date, cItem, end_date, time_zone, duration, location,
 * num_resources, people_ids, resource_ids, company, available, item_link
 *
 * @return {Object} extra - Object to be sent to the API
 */
function getQueryParameters(params) {
    const extra = {};

    extra.duration = params.duration;
    extra.num_resources = params.num_resources;
    extra.date = extra.start_date = bbTimeZone.toCompanyDate(params.start_date);
    extra.person_group_id = params.cItem.person_group_id;
    extra.assets = [];

    if (params.location) {
        extra.location = params.location;
    }

    if (params.service_id) {
        extra.service_id = params.service_id;
    }

    if (params.company) {
        extra.company_id = params.company.id;
    }

    if (params.cItem.event_id) {
        extra.event_id = params.cItem.event_id;
    }

    if (
        params.cItem.person &&
        !params.cItem.anyPerson() &&
        !params.item_link.event_id &&
        !extra.event_id
    ) {
        extra.person_id = params.cItem.person.id;
    }

    if (
        params.cItem.resource &&
        !params.cItem.anyResource() &&
        !params.item_link.event_id &&
        !extra.event_id
    ) {
        extra.resource_id = params.cItem.resource.id;
    }

    if (params.end_date) {
        extra.end_date = bbTimeZone.toCompanyDate(params.end_date);
    }

    if (params.time_zone) {
        extra.time_zone = params.time_zone;
    }

    if (params.cItem && params.cItem.id) {
        if (params.cItem instanceof BBModel.Purchase.Booking || params.cItem instanceof BBModel.BasketItem) {
            extra.ignore_booking = params.cItem.id;
        } else if (params.cItem instanceof BBModel.ServiceItem) {
            extra.ignore_service_item = params.cItem.id;
        }
    }

    if (params.people_ids) {
        extra.people_ids = params.people_ids;
    }

    if (params.resource_ids) {
        extra.resource_ids = params.resource_ids;
    }

    if (params.person_id) {
        extra.person_id = params.person_id;
    }

    if (params.resource_id) {
        extra.resource_id = params.resource_id;
    }

    if (params.cItem.person_group_id) {
      extra.assets.push({person_group_id: params.cItem.person_group_id});
    }

    return extra;
}

/**
 * @description Merges raw time data from API
 * and resolves any outstanding child promises
 *
 * @param {Array} times - An array of BaseResource objects
 * which contain an array of time data
 * @param {Object} item - A BasketItem object for the item queried
 * @param {Object} day - A BaseResource object representing a day
 * @param {Object} company - Current company
 *
 * @return Void
 */
function handleTimes(times, item, day, company, resolve) {
    if (times.length) {
        if (personSelectionService.isPeopleGroupConfigured()) {
            company.$getPeople().then((people) => {
                personSelectionService.setPeople(people, item.service);
                times = mergeTimes(times, item.service, day.date);
                resolve(times);
            });
        } else {
            times = mergeTimes(times, item.service, day.date);
            resolve(times);
        }
    } else {
        resolve([]);
    }
}

/**
 * @description Merges raw time data from API for results
 * containing 'date_links' and resolves any outstanding child promises
 *
 * @param {Array} all_days - An array of days with time data
 * @param {Object} currentItem - A BasketItem object for the item queried
 * @param {Object} company - Current company
 *
 * @return {Array} An array of promises
 */
function handleDateLinks(all_days, currentItem, company) {
    const all_days_def = [];

    for (const day of all_days) {
        // there's several days - get them all
        all_days_def.push(new Promise((resolve) => {
            if (day.$has('event_links')) {
                day.$get('event_links').then((all_events) => {
                    const availableEvents = clearUnavailableTimes(all_events);

                    handleTimes(availableEvents, currentItem, day, company, resolve);
                });
            } else if (day.times) {
                handleTimes(clearUnavailableTimes([day]), currentItem, day, company, resolve);
            }
        }));
    }

    return all_days_def;
}

/**
 * @description Check every bookable item and remove items
 * with no time availability
 *
 * @param {Array} allEvents - An array of BaseResources of bookable items
 *
 * @return {Array} filteredEvents - A filtered BaseResources array
 */
function clearUnavailableTimes(all_events) {
    const filteredEvents = [];

    all_events.forEach((event) => {
        const availableTimes = [];

        if (event.times.length) {
            event.times.forEach((time) => {
                if (time.avail) {
                    availableTimes.push(time);
                }
            });

            if (availableTimes.length) {
                event.times = availableTimes;

                filteredEvents.push(event);
            }
        }
    });

    return filteredEvents;
}

async function queryDates({item_link}, extra, resolve, reject) {

    let linkRel;
    if (item_link.$has('service_items_dates')) {
        linkRel = 'service_items_dates';
    } else if (item_link.$has('dates')) {
        linkRel = 'dates';
    }

  if (linkRel) {
      try {
        const resource = await item_link.$get(linkRel, extra);
        if (resource && resource.dates) {
          const slots = resource.dates.map((day) => {
            return new BBModel.Day(day);
          });
          resolve(slots);
        }
      } catch (err) {
          reject(err);
      }
  } else {
      console.warn('Unable to retrieve dates: company does not have a \'dates\' link. Dates only exist in 5.9 or higher');
      reject('No dates data');
  }
}

async function queryDayTimes({item_link}, extra, resolve, reject) {
    extra.no_cache = true;
    let linkRel;

    if (item_link.$has('service_items_times')) {
        linkRel = 'service_items_times';
    } else if (item_link.$has('times')) {
        linkRel = 'times';
    }

    if (linkRel) {
        try {
          const resource = await item_link.$get(linkRel, extra);
          const dates = await resource.$get('times');

          const slots = dates.map((time) => {
              time.datetime = time.start;
              time.avail = time.available;
              const timeMatch = time.start.match(/\d\d:\d\d/);
              if (timeMatch) time.time = timeMatch[0];
              return new BBModel.TimeSlot(time);
          });

          resolve(slots);

        } catch (err) {
            reject(err);
        }
    } else {
        console.warn('Unable to retrieve times: service does not have a \'times\' link.');
        reject('No times data');

    }
}

/**
 * @description Handles querying time api for general conditions
 *
 * @param {Object} queryDefaults - The values required to make a query
 * @param {Object} extra - The payload to be sent to the API
 * to make a valid query
 * values can consist of the following:
 * date, start_date, cItem, end_date, time_zone, duration, location,
 * num_resources, people_ids, resource_ids, company, available, item_link
 * @param {Deferred} deferred - Used to resolve or reject promise
 * from query function
 *
 * @return Void
 */
function queryTime(queryDefaults, extra, resolve, reject) {
    let dateLinksFound = false;
    let eventLinksFound = false;
    let embeddedTimes = false;
    let linkRel;

    if (queryDefaults.item_link.$has('service_items_times')) {
        linkRel = 'service_items_times';
    } else if (queryDefaults.item_link.$has('times')) {
        linkRel = 'times';
    }

    queryDefaults.item_link
        .$get(linkRel, extra)
        .then((results) => {

            if (results.$has('date_links')) {
                dateLinksFound = true;

                return results.$get('date_links');
            }

            if (results.$has('event_links')) {
                eventLinksFound = true;

                return results.$get('event_links');
            }

            if (results.times) {
                embeddedTimes = true;

                return Promise.resolve(results.times);
            }

            if (results.$has('service_items_times')) {
                embeddedTimes = true;

                return results.$get('service_items_times');
            }

            if (results.$has('times')) {
                embeddedTimes = true;

                return results.$get('times');
            }

            return results;
        })
        .then((results) => {
            if (dateLinksFound) {
                const all_days_def = handleDateLinks(
                    results,
                    queryDefaults.cItem,
                    queryDefaults.company
                );

                return Promise.all(all_days_def);
            } else if (eventLinksFound) {
                return new Promise((resolve) => {
                    handleTimes(
                        results,
                        queryDefaults.cItem,
                        queryDefaults.start_date,
                        queryDefaults.company,
                        resolve
                    );
                });
            } else if (embeddedTimes) {
                const item = queryDefaults.cItem;
                results = results.map((time) => {
                    time.datetime = time.start;
                    time.avail = time.available;
                    const timeMatch = time.start.match(/\d\d:\d\d/);
                    if (timeMatch) time.time = moment.tz(time.datetime, queryDefaults.company.timezone).format('HH:mm');
                    return new BBModel.TimeSlot(time, item.service);
                });
                return results;
            }
        })
        .then((times) => {
            const date_times = {};

            /* build day/slot array ensuring slots are grouped
               by the display time zone */
            let dateTimeArray = [];
            times.forEach(time => dateTimeArray = dateTimeArray.concat(time));
            dateTimeArray.sort((a, b) => a.datetime.unix() - b.datetime.unix());
            dateTimeArray.forEach((slot) => {
                const dateISOString = bbTimeZone.toDisplayDate(slot.datetime);
                if (date_times[dateISOString]) {
                    date_times[dateISOString].push(slot);
                } else {
                    date_times[dateISOString] = [slot];
                }
            });

            /* add days back that don't have any availabiity
               and return originally requested range only */
            const newDateTimes = {};

            let startDateClone = originalStartDate || queryDefaults.start_date.clone();

            while (startDateClone <= queryDefaults.end_date) {
                const dateISO = bbTimeZone.toDisplayDate(startDateClone);

                newDateTimes[dateISO] = date_times[dateISO]
                    ? date_times[dateISO]
                    : [];
                startDateClone = startDateClone.clone().add(1, 'day');
            }

            resolve(newDateTimes);
        })
        .catch((err) => {
            reject(err);
        });
}

/**
 * @description Combines the time data with bookable items
 *
 * @param {Array} allEvents - A array of BaseResources of bookable items
 * @param {Object} service - A Service object associated with the time data
 * @param {String} data - The date being processed in the format "2017-08-24"
 *
 * @return {Array} timeSlots - An array of TimeSlot objects
 * containing details of the times available for booking
 */
function mergeTimes(allEvents, service, date) {
    let hasGroupPriority;

    const times = buildTimes(allEvents, service);
    const groupedStaff = groupStaffForTime(times);

    if (personSelectionService.isPeopleGroupConfigured()) {
        hasGroupPriority = true;
    }

    const filteredTimes = filterTimes(groupedStaff, hasGroupPriority);
    const detailedTimes = buildDetailedTimes(times, filteredTimes);
    const timeSlots = convertTimesToTimeSlotModels(
        detailedTimes,
        service,
        date
    );

    return timeSlots;
}

/**
 * @description Converts time data objects into TimeSlot model objects
 *
 * @param {Array} sortedTimes - An array of objects containing
 * time data ordered by time
 * @param {Object} service - A Service object associated with the time data
 * @param {String} date - The date being processed in the format "2017-08-24"
 *
 * @return {Array} timeSlots - an array of TimeSlot objects
 * converted form sortedTimes parameter
 */
function convertTimesToTimeSlotModels(sortedTimes, service, date) {
    const timeSlots = [];

    sortedTimes.forEach((rawTimeSlot) => {
        if (rawTimeSlot) {
            if (!rawTimeSlot.datetime) {
                rawTimeSlot.datetime =
                    convertTimeToMoment(
                        moment(date),
                        rawTimeSlot.time
                    );
            }

            timeSlots.push(new BBModel.TimeSlot(rawTimeSlot, service));
        }
    });

    return timeSlots;
}

/**
 * @description Build a times list from events resources
 *
 * @param {Array} allEvents - An array of BaseResources of bookable items
 *
 * @return {Array} times - A list with time availabilities objects
 */
function buildTimes(allEvents) {
    const times = [];

    allEvents.forEach((event) => {
        event.times.forEach((time) => {
            time.staff = event.name;
            time.event_id = event.event_id;

            times.push(time);
        });
    });

    return times;
}

/**
 * @description Build an object with grouped staff members
 * that have the same time availability
 *
 * @param {Array} times - A list with time availabilities objects
 *
 * @return {Object} groupedStaff - An object with grouped staff names strings
 */
function groupStaffForTime(times) {
    const groupedStaff = {};

    times.forEach((time) => {
        if (groupedStaff[time.time]) {
            groupedStaff[time.time].push(time.staff);
        } else {
            groupedStaff[time.time] = [time.staff];
        }
    });

    return groupedStaff;
}

/**
 * @description Filter time availabilities assigning
 * a single staff member randomly or according to priorities
 *
 * @param {Object} groupedStaff - An object with grouped staff names strings
 * @param {Boolean} hasGroupPriority - True if the groups has priorities
 *
 * @return {Object} filteredTimes - An object with times
 * and the coresponding staff member name
 */
function filterTimes(groupedStaff, hasGroupPriority) {
    const filteredTimes = {};
    let availablePeopleForTime;

    Object.keys(groupedStaff).forEach((time) => {
        if (hasGroupPriority) {
            availablePeopleForTime = personSelectionService.getAvailablePeople(
                groupedStaff[time]
            );
        } else {
            availablePeopleForTime = groupedStaff[time];
        }
        filteredTimes[time] = personSelectionService.getRandomPerson(
            availablePeopleForTime
        );
    });

    return filteredTimes;
}

/**
 * @description Build a detailed times array with staff member details
 *
 * @param {Array} times - A list with time availabilities objects
 * @param {Object} filteredTimes - An object with times
 * and the coresponding staff member name
 *
 * @return {Array} detailedTimes - An array with times
 */
function buildDetailedTimes(times, filteredTimes) {
    const detailedTimes = [];

    Object.keys(filteredTimes).forEach((timeId) => {
        const assignedStaffName = filteredTimes[timeId];
        const timeDetails = getTimeDetailsForStaff(
            times,
            assignedStaffName,
            timeId
        );

        timeDetails.person = personSelectionService.filterPeopleByPerson(
            assignedStaffName
        );

        detailedTimes.push(timeDetails);
    });

    return detailedTimes;
}

/**
 * @description Retrieve a detailed time object
 * for a corresponding staff member name
 *
 * @param {Array} times - A list with time availabilities objects
 * @param {String} assignedStaffName - A staff member name
 * @param {String} timeId - A time id
 *
 * @return {Object} A time object
 */
function getTimeDetailsForStaff(times, assignedStaffName, timeId) {
    timeId = parseInt(timeId);

    const matchedTimes = times.filter((time) => {
        return time.staff === assignedStaffName && time.time === timeId;
    });

    // Getting random slot from all slots with matched staff.
    return matchedTimes[Math.floor(Math.random() * matchedTimes.length)];
}


const time = {
    query,
    queryItems,
    getQueryParameterDefaults,
    getQueryParameters,
    handleTimes,
    handleDateLinks,
    clearUnavailableTimes,
    queryTime,
    mergeTimes,
    convertTimesToTimeSlotModels,
    buildTimes,
    groupStaffForTime,
    filterTimes,
    buildDetailedTimes,
    getTimeDetailsForStaff
};

export {
    time
};
